import { CloseOutlined, MoreOutlined, PrinterOutlined } from '@ant-design/icons';
import { DeleteButtonProps, EditButtonProps, RefreshButton } from '@refinedev/antd';
import { Button, ButtonProps, Flex, Tooltip } from 'antd';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PopupMenu from '@/components/general/PopupMenu';
import PackageMessages from '@/components/logistic/PackageMessages/PackageMessages';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { useUiContext } from '@/contexts/UiContext';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import useShipmentHeaderActions from './useShipmentHeaderActions';

interface ShipmentShowHeaderButtonsProps {
  record: Shipment;
  refineButtonsProps: {
    editButtonProps: EditButtonProps | undefined;
    deleteButtonProps: DeleteButtonProps | undefined;
  };
  refetch: () => void;
  blockChangeState: { id: string; message: string }[];
  canSend?: boolean;
  isPaid?: boolean;
  showQuantityAlert?: boolean;
  /** Metadata: indica si el archivo tributario fue cargado, para bloquear avance de estado */
  hasTaxFileUploaded?: boolean;
}

const ShipmentShowHeaderButtons = (props: ShipmentShowHeaderButtonsProps) => {
  const { isMediumScreen } = useUiContext();
  const { packageRepository } = useRepositoriesContext();
  const { t } = useTranslation();
  const {
    record,
    refineButtonsProps,
    refetch,
    blockChangeState,
    canSend,
    isPaid,
    showQuantityAlert,
    hasTaxFileUploaded,
  } = props;
  const { editButtonProps, deleteButtonProps } = refineButtonsProps;
  const { id, status } = record;
  const {
    changeStateButtonsProps,
    changeStateBlockTooltip,
    disableStateButtons,
    showCancelButton,
    onCancelClick,
    contextHolder,
    notificationContextHolder,
  } = useShipmentHeaderActions({
    record,
    refetch,
    blockChangeState,
    canSend,
    isPaid,
    showQuantityAlert,
    hasTaxFileUploaded,
  });
  // --- Botones de cambio de estado renderizados ---
  const changeStateButtons = useMemo(
    () =>
      changeStateButtonsProps.length > 0
        ? changeStateButtonsProps.map((btnProps: ButtonProps, index) => (
            <Tooltip key={index} title={changeStateBlockTooltip || ''}>
              <Button
                key={'change-state-' + index}
                type={'primary'}
                color={isMediumScreen ? 'primary' : undefined}
                {...btnProps}
                disabled={disableStateButtons || btnProps.disabled}
              />
            </Tooltip>
          ))
        : null,
    [changeStateBlockTooltip, changeStateButtonsProps, disableStateButtons, isMediumScreen],
  );
  // --- Botón de cancelar ---
  const cancelButton = useMemo(
    () =>
      showCancelButton ? (
        <Tooltip title={changeStateBlockTooltip}>
          <Button
            key={'cancel'}
            disabled={disableStateButtons}
            danger
            icon={<CloseOutlined />}
            onClick={onCancelClick}
            type={isMediumScreen ? 'text' : 'default'}>
            {t('Cancelar')}
          </Button>
        </Tooltip>
      ) : null,
    [
      showCancelButton,
      changeStateBlockTooltip,
      disableStateButtons,
      onCancelClick,
      isMediumScreen,
      t,
    ],
  );
  // --- Contenido del menú popup ---
  const popupMenuContent: ReactNode[] = useMemo(() => {
    const buttons: ReactNode[] = [];

    if (isMediumScreen && changeStateButtons) {
      buttons.push(changeStateButtons);
    }

    if (
      status === PACKAGE_STATES.PENDIENTE ||
      status === PACKAGE_STATES.PREPARACION ||
      status === PACKAGE_STATES.PREPARADO
    ) {
      buttons.push(
        <Button
          key={'print'}
          icon={<PrinterOutlined />}
          type="text"
          onClick={() => id && void packageRepository.printShippingLabel(id, RESOURCES.SHIPMENTS)}>
          {t('Imprimir etiqueta')}
        </Button>,
      );
    }

    if (status === PACKAGE_STATES.PENDIENTE) {
      buttons.push(<ProtectedEditButton key={'edit'} {...editButtonProps} type="text" />);
    }

    if (isMediumScreen && cancelButton) {
      buttons.push(cancelButton);
    }

    if (status === PACKAGE_STATES.PENDIENTE) {
      buttons.push(<ProtectedDeleteButton key={'delete'} {...deleteButtonProps} type="text" />);
    }

    return buttons;
  }, [
    cancelButton,
    changeStateButtons,
    deleteButtonProps,
    editButtonProps,
    id,
    isMediumScreen,
    packageRepository,
    status,
    t,
  ]);

  return (
    <Flex gap={8} wrap align="center" style={{ minHeight: '40px' }}>
      <RefreshButton
        key="refresh"
        onClick={() => void refetch()}
        hideText
        size={isMediumScreen ? 'small' : 'middle'}
      />
      {!isMediumScreen && (
        <>
          {cancelButton} {changeStateButtons}
        </>
      )}

      {popupMenuContent.length > 0 && (
        <PopupMenu
          openButton={<Button icon={<MoreOutlined />} size={isMediumScreen ? 'small' : 'middle'} />}
          disabled={disableStateButtons}
          tooltip={changeStateBlockTooltip}
          menuContent={
            <Flex vertical align="start" gap={8}>
              {popupMenuContent}
            </Flex>
          }
        />
      )}
      {contextHolder}
      {notificationContextHolder}
      <PackageMessages packageId={id} type="shipment" />
    </Flex>
  );
};

export default ShipmentShowHeaderButtons;
